import React, { useMemo, useState } from 'react';
import {
    Alert,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Divider,
    Stack,
    ToggleButton,
    ToggleButtonGroup,
    Typography
} from '@mui/material';
import PaymentsIcon from '@mui/icons-material/Payments';
import CreditCardIcon from '@mui/icons-material/CreditCard';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import { OpenModalHookType } from '@hooks/useOpenModal';
import { Order } from '@api/Orders/Order';
import { PaymentMethod, getOrderPaidTotal } from '@api/Orders/OrderPayments';
import { getPendingToCollect } from '@api/Orders/OrderPaymentGate';
import { currencyFormatter } from '@utils/Numbers';
import { getOrderShortNumber } from '@utils/OrdersBoard';
import Calculator from '@components/Utilities/Calculator';

// ============================================================================
// "¿Cómo se cobró?" — el paso que faltaba antes de completar un pedido.
//
// ## Cómo se decidió (Irving, 2026-09-11)
// *"Que cuando se quiera completar mesa, pida el pago, pero que no sea
// bloqueante, que se pueda omitir de alguna manera. Para no bloquearlos pero sí
// avisarles que registren el pago."*
//
// Por eso este modal **no tiene candado**: el botón de omitir está a la vista,
// no escondido detrás de una confirmación. Lo que sí hace es que omitir sea una
// DECISIÓN —dice qué se pierde y deja su renglón en la bitácora— en vez de un
// descuido invisible, que es lo que pasaba hasta hoy.
//
// ## Decisiones de la UI, y el porqué
// - **Un toque para el caso normal**: el monto llega precargado con lo que falta
//   y los tres métodos son botones. Cobrar una mesa en efectivo son dos toques.
// - **La calculadora solo aparece si el cobro es PARCIAL**, porque teclear un
//   monto es el caso raro y ponerla siempre convierte dos toques en cinco.
// - ⚠️ **Nada de `color="secondary"`**: en este tema `palette.secondary.main` es
//   `#FFFFFF` puro, así que un `contained secondary` es blanco sobre blanco. Ya
//   pasó con el botón "Gasto" de la calculadora y con el "Descargar" del recibo.
// ============================================================================

interface ModalRegisterPaymentProps {
    modalRegisterPayment: OpenModalHookType;
    order?: Order;
    /** Registra el cobro y completa. El modal ya se cerró cuando esto corre. */
    onRegister: (method: PaymentMethod, amount: number) => void;
    /** Completar SIN registrar cómo se cobró. Deja rastro en la bitácora. */
    onSkip: () => void;
}


const METODOS: { method: PaymentMethod; label: string; icon: React.ReactNode }[] = [
    { method: PaymentMethod.Cash, label: 'Efectivo', icon: <PaymentsIcon /> },
    { method: PaymentMethod.CreditCard, label: 'Tarjeta', icon: <CreditCardIcon /> },
    { method: PaymentMethod.BankTransfer, label: 'Transferencia', icon: <AccountBalanceIcon /> }
];

export const ModalRegisterPayment: React.FunctionComponent<ModalRegisterPaymentProps> = (props) => {
    const { modalRegisterPayment, order, onRegister, onSkip } = props;
    const [method, setMethod] = useState<PaymentMethod>(PaymentMethod.Cash);
    const [parcial, setParcial] = useState<boolean>(false);
    const [montoParcial, setMontoParcial] = useState<number>(0);

    const pendiente = useMemo(() => getPendingToCollect(order), [order]);
    const yaCobrado = useMemo(() => getOrderPaidTotal(order), [order]);
    const monto = parcial ? montoParcial : pendiente;

    const cerrar = () => {
        setParcial(false);
        setMontoParcial(0);
        setMethod(PaymentMethod.Cash);
        modalRegisterPayment.close();
    };

    const registrar = () => {
        cerrar();
        onRegister(method, monto);
    };

    const omitir = () => {
        cerrar();
        onSkip();
    };

    return (
        <Dialog
            PaperProps={{ style: { borderRadius: 15, padding: 4 } }}
            open={modalRegisterPayment.modalOpen}
            onClose={cerrar}
            fullWidth
            maxWidth="xs"
            aria-labelledby="modal-register-payment-title"
        >
            <DialogTitle id="modal-register-payment-title">
                <Typography variant="h6" fontWeight={600}>
                    ¿Cómo se cobró?
                </Typography>
                <Typography variant="body2" color="text.secondary">
                    {order?.tableKiosk?.name
                        ? `${order.tableKiosk.name} · pedido #${getOrderShortNumber(order)}`
                        : `Pedido #${order ? getOrderShortNumber(order) : ''}`}
                </Typography>
            </DialogTitle>
            <DialogContent>
                <Stack spacing={2}>
                    <Stack direction="row" justifyContent="space-between" alignItems="baseline">
                        <Typography>Falta por cobrar</Typography>
                        <Typography variant="h5" fontWeight={700}>
                            {currencyFormatter(pendiente)}
                        </Typography>
                    </Stack>
                    {yaCobrado > 0 && (
                        <Typography variant="body2" color="text.secondary">
                            Ya tenía registrado {currencyFormatter(yaCobrado)}
                        </Typography>
                    )}
                    <Divider />
                    <ToggleButtonGroup
                        exclusive
                        fullWidth
                        color="primary"
                        value={method}
                        onChange={(_, value) => value && setMethod(value as PaymentMethod)}
                    >
                        {METODOS.map((opcion) => (
                            <ToggleButton key={opcion.method} value={opcion.method} sx={{ py: 1.2 }}>
                                <Stack alignItems="center" spacing={0.5}>
                                    {opcion.icon}
                                    <Typography variant="caption" fontWeight={600}>
                                        {opcion.label}
                                    </Typography>
                                </Stack>
                            </ToggleButton>
                        ))}
                    </ToggleButtonGroup>
                    {parcial ? (
                        <Stack alignItems="center" spacing={1}>
                            <Typography variant="body2" color="text.secondary">
                                Monto cobrado con {METODOS.find((o) => o.method === method)?.label.toLowerCase()}
                            </Typography>
                            <Calculator onChangeValue={setMontoParcial} />
                            <Button size="small" onClick={() => setParcial(false)}>
                                Cobrar el total
                            </Button>
                        </Stack>
                    ) : (
                        <Button size="small" onClick={() => setParcial(true)}>
                            Se cobró solo una parte
                        </Button>
                    )}
                    <Alert severity="warning" icon={false} sx={{ py: 0.5 }}>
                        <Typography variant="caption">
                            Si lo completas sin registrar el cobro, el corte de caja no va a cuadrar y el informe lo
                            va a contar como pedido sin pago.
                        </Typography>
                    </Alert>
                </Stack>
            </DialogContent>
            <DialogActions>
                <Stack width="100%" spacing={1}>
                    <Button
                        fullWidth
                        variant="contained"
                        color="success"
                        disabled={!(monto > 0)}
                        onClick={registrar}
                    >
                        {`Registrar ${currencyFormatter(monto)} y completar`}
                    </Button>
                    <Button fullWidth variant="text" color="inherit" onClick={omitir}>
                        Completar sin registrar el cobro
                    </Button>
                </Stack>
            </DialogActions>
        </Dialog>
    );
};

export default ModalRegisterPayment;
